/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/dashboards/ZoneFilter.tsx
 * PURPOSE: Zone/WorkArea dropdown filter for Manager Dashboard
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-04-11
 * COMPLIANCE: SDLC 6.3.1 + BR-DASH-001 + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager Dashboard — filter staff + KPIs by work area/zone
 * - Impact: MEDIUM — BKL Pilot usability (ISSUE-009 Step 2)
 * - Stakeholders: Branch Managers (BKL GM Huy)
 *
 * @technical-architecture
 * - Layer: Presentation Component (separate file — CTO F1 merge owner policy)
 * - Pattern: TanStack Query + controlled dropdown
 * - Dependencies: React 18, apiV2, useQuery
 *
 * @cross-references
 * - Design: ISSUE-009 Step 2, BR-DASH-001
 * - Sprint: SPRINT-095L S95L-008 (3 SP)
 * - Backend: GET /api/v2/tenant-admin/organizations/{branch_id}/children
 * - Integration: ManagerDashboard.tsx imports this component (Local team)
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiV2 } from '../../services/api/config';

// ─── Types ──────────────────────────────────────────────────────────────

interface WorkArea {
  id: string;
  name: string;
  organization_type: string;
}

interface ZoneFilterProps {
  branchId: string | null;
  onZoneChange: (zoneId: string | null) => void;
  selectedZoneId?: string | null;
}

// ─── Component ──────────────────────────────────────────────────────────

export const ZoneFilter: React.FC<ZoneFilterProps> = ({
  branchId,
  onZoneChange,
  selectedZoneId = null,
}) => {
  const { data: workAreas, isLoading } = useQuery<WorkArea[]>({
    queryKey: ['manager', 'work-areas', branchId],
    queryFn: async () => {
      if (!branchId) return [];
      // Fetch full org tree then find direct children of this branch (= work areas)
      const response = await apiV2.get('/tenant-admin/organizations/tree');
      const tree: any[] = Array.isArray(response.data) ? response.data : [response.data];
      // Find branch node in tree, return its direct children as work areas
      const findBranchChildren = (nodes: any[]): WorkArea[] => {
        for (const node of nodes) {
          if (node.id === branchId && node.children?.length > 0) {
            return node.children.map((c: any) => ({
              id: c.id,
              name: c.name,
              organization_type: c.organization_type ?? 'work_area',
            }));
          }
          if (node.children?.length > 0) {
            const found = findBranchChildren(node.children);
            if (found.length > 0) return found;
          }
        }
        return [];
      };
      return findBranchChildren(tree);
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000,
  });

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onZoneChange(value === '' ? null : value);
  };

  if (!branchId) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <label
        htmlFor="zone-filter"
        style={{
          fontSize: '14px',
          fontWeight: 500,
          color: 'var(--nqh-text-secondary, #6b7280)',
        }}
      >
        Khu vực:
      </label>
      <select
        id="zone-filter"
        data-testid="zone-filter"
        value={selectedZoneId ?? ''}
        onChange={handleChange}
        disabled={isLoading}
        aria-label="Lọc theo khu vực làm việc"
        style={{
          padding: '6px 12px',
          borderRadius: '8px',
          border: '1px solid var(--nqh-border, #d1d5db)',
          fontSize: '14px',
          backgroundColor: 'var(--nqh-bg-surface, #ffffff)',
          color: 'var(--nqh-text-primary, #111827)',
          minWidth: '160px',
        }}
      >
        <option value="">Tất cả khu vực</option>
        {workAreas?.map((area) => (
          <option key={area.id} value={area.id}>
            {area.name}
          </option>
        ))}
      </select>
      {isLoading && (
        <span style={{ fontSize: '12px', color: '#9ca3af' }}>
          Đang tải...
        </span>
      )}
    </div>
  );
};

export default ZoneFilter;
